import { useMemo } from "react";
import { useQuery } from "@apollo/client";

import { GET_MISSION_CONTROL_DATA } from "shared/UserGuides/gql";
import { collectOverallTimeToComplete, parseMinutes } from "shared/UserGuides/utils";

type Objective = {
  id: string;
  groupId: string;
  title: string;
  timeToComplete: string;
  isInProgress: boolean;
};

type MissionsProgressData = {
  overallTimeToComplete: string;
  completedCount: number;
  totalCount: number;
  nextObjective: Objective | undefined;
  isAllCompleted: boolean;
  loading: boolean;
  error: boolean;
  refetch: () => void;
};

const useMissionsProgressData = (): MissionsProgressData => {
  const { data, loading, error, refetch } = useQuery(GET_MISSION_CONTROL_DATA);

  return useMemo(() => {
    if (!data) {
      return {
        overallTimeToComplete: "",
        completedCount: 0,
        totalCount: 0,
        nextObjective: undefined,
        isAllCompleted: false,
        loading,
        error: !!error,
        refetch,
      };
    }

    const { userGuideGroups, completedUserGuides, activeUserGuideProgress } = data;

    const completedIds = new Set(completedUserGuides.map((c) => c.guide.id));
    const allGuides = userGuideGroups.flatMap((g) => g.chapters.flatMap((ch) => ch.guides));
    const totalCount = allGuides.length;
    const completedCount = allGuides.filter((g) => completedIds.has(g.id)).length;

    const overallTimeToComplete = collectOverallTimeToComplete(userGuideGroups, [...completedIds]);
    const isAllCompleted = completedCount === totalCount && totalCount > 0;

    const guidesWithGroupId = userGuideGroups.flatMap((group) =>
      group.chapters.flatMap((chapter) =>
        chapter.guides.map((guide) => ({
          ...guide,
          groupId: group.id,
          groupOrder: group.order,
          chapterOrder: chapter.order,
        }))
      )
    );

    let nextObjective: Objective | undefined;

    if (activeUserGuideProgress) {
      const active = guidesWithGroupId.find((g) => g.id === activeUserGuideProgress.guide.id);

      if (active) {
        nextObjective = {
          id: active.id,
          groupId: active.groupId,
          title: active.metadata.title,
          timeToComplete: parseMinutes(active.metadata.minutesToComplete),
          isInProgress: true,
        };
      }
    }

    if (!nextObjective && !isAllCompleted) {
      const nextGuide = [...guidesWithGroupId]
        .sort(
          (a, b) =>
            a.groupOrder - b.groupOrder || a.chapterOrder - b.chapterOrder || a.order - b.order
        )
        .find((g) => !completedIds.has(g.id));

      if (nextGuide) {
        nextObjective = {
          id: nextGuide.id,
          groupId: nextGuide.groupId,
          title: nextGuide.metadata.title,
          timeToComplete: parseMinutes(nextGuide.metadata.minutesToComplete),
          isInProgress: false,
        };
      }
    }

    return {
      overallTimeToComplete,
      completedCount,
      totalCount,
      nextObjective,
      isAllCompleted,
      loading,
      error: !!error,
      refetch,
    };
  }, [data, loading, error, refetch]);
};

export default useMissionsProgressData;
